import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 420,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
  className?: string;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <DialogPrimitive.Content
          // Radix wires aria-describedby to the description only when one is
          // rendered, and omits the attribute otherwise, so the caveat a sighted
          // reader sees ("not yet persisted to the backend") reaches a screen
          // reader too. Suppressing it outright hid that copy from assistive
          // technology whenever a description was supplied.
          className={cn(
            "fixed right-0 top-0 z-50 flex h-full flex-col border-l border-line bg-surface shadow-sheet outline-none focus-visible:outline-none",
            className
          )}
          style={{ width: `min(100%, ${width}px)` }}
        >
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-base font-bold text-ink">{title}</DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="mt-0.5 text-sm leading-relaxed text-muted">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              aria-label="Close drawer"
              className="grid size-7 shrink-0 place-items-center rounded-md text-muted transition-colors hover:bg-bg hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
            >
              <X aria-hidden className="size-4" />
            </DialogPrimitive.Close>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4 text-sm text-ink">{children}</div>
          {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-line bg-surface px-5 py-4">{footer}</footer>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}